import { createSelector } from "reselect"
import { get, find, chain, map } from "lodash"
import { percentageFormatter } from "helpers/formatters/numbers"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { getComparisonFactorsData, tableDownloadDataReducer } from "helpers/comparison"
import { getFavoriteFactors, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { getFactorsMetadata } from "selectors/factors"
import { getModelCategories, getModelFactorsClassification } from "selectors/modelMetadata"
import { getSelectedMetric } from "./metrics"
import { getItems } from "./items"
import { getData0, getData1, getData2, getData3, getData4 } from "./data"

const getSelectedDate = (state, props) => props.selectedDate

function getDailyData(selectedDate, data, categories, mustAggregateFactors, factorsClassification) {
  const date = find(data, d => d.date === selectedDate)
  if (!date) {
    return null
  }

  let factors = get(date, "attribution.factors")
  if (mustAggregateFactors) {
    factors = aggregateFactorsSumBy(factorsClassification, factors, "value")
  }

  return {
    summary: {
      total: date.total,
      factors: date.attribution.summary.factors,
      specific: date.attribution.summary.specific,
    },
    categories: chain(categories)
      .map(category => ({
        id: category.id,
        value: chain(factors)
          .filter(f => f.category === category.id)
          .sumBy("value")
          .value(),
      }))
      .keyBy("id")
      .mapValues("value")
      .value(),
    factors: chain(factors)
      .keyBy("id")
      .mapValues("value")
      .value(),
  }
}

const getDailyData0 = createSelector([getSelectedDate, getData0, getModelCategories, getMustAggregateFactors, getModelFactorsClassification], getDailyData)
const getDailyData1 = createSelector([getSelectedDate, getData1, getModelCategories, getMustAggregateFactors, getModelFactorsClassification], getDailyData)
const getDailyData2 = createSelector([getSelectedDate, getData2, getModelCategories, getMustAggregateFactors, getModelFactorsClassification], getDailyData)
const getDailyData3 = createSelector([getSelectedDate, getData3, getModelCategories, getMustAggregateFactors, getModelFactorsClassification], getDailyData)
const getDailyData4 = createSelector([getSelectedDate, getData4, getModelCategories, getMustAggregateFactors, getModelFactorsClassification], getDailyData)

const getFactorsData = createSelector(
  [getSelectedDate, getModelCategories, getFavoriteFactors, getData0, getData1, getData2, getData3, getData4],
  (selectedDate, categories, favoriteFactors, data, data1, data2, data3, data4) =>
    getComparisonFactorsData(selectedDate, categories, favoriteFactors, "attribution.factors", data, data1, data2, data3, data4),
)

export const getDetailsItems = createSelector(
  [getFactorsMetadata, getSelectedMetric],
  (factors, selectedMetric) => ([{
    items: [{
      label: "Total Risk",
      fn: item => percentageFormatter(get(item, "summary.total", 0)),
    }],
  }, {
    label: "Risk Decomposition",
    defaultOpen: true,
    items: [{
      label: "Specific",
      fn: item => percentageFormatter(get(item, "summary.specific", 0)),
    }, {
      label: "Factors",
      fn: item => percentageFormatter(get(item, "summary.factors", 0)),
    }, ...(factors[selectedMetric.id] ? [{
      label: selectedMetric.label,
      fn: item => percentageFormatter(get(item, ["factors", selectedMetric.id], 0)),
    }] : [])],
  }]),
)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => ([data0, data1, data2, data3, data4]),
)

export const getFactorsCategories = createSelector(
  [getModelCategories, getFactorsData, getFavoriteFactors],
  (categories, factors, favoriteFactors) => chain(categories)
    .filter(category => category.id !== "esg")
    .map((category) => ({
      ...category,
      getter: item => percentageFormatter(get(item, ["categories", category.id], 0)),
      factors: chain(factors)
        .get(category.id)
        .map(factor => ({
          ...factor,
          getter: factor.fn,
          favorite: get(favoriteFactors, factor.id, false),
        }))
        .orderBy(["favorite", f => f.priorityOrder || 0, f => Math.abs(f.value)], ["desc", "desc", "desc"])
        .value(),
    }))
    .value(),
)

export const getTableDetailsData = createSelector(
  [getItems, getDetailsData, getDetailsItems],
  (items, data, detailsItems) => tableDownloadDataReducer(items, data, detailsItems, "items"),
)

const getTableFactorsData = createSelector(
  [getItems, getDetailsData, getFactorsCategories],
  (items, data, factorsCategories) => tableDownloadDataReducer(items, data, factorsCategories, "factors"),
)

export const getTableDownloadData = createSelector(
  [getItems, getTableDetailsData, getTableFactorsData],
  (items, tableDetailsData, tableFactorsData) => {
    const headers = ["Metric Group", "Metric", ...map(items, "label")]
    return {
      sheets: [{
        name: "Risk",
        rows: [
          headers,
          ...tableDetailsData,
          ...tableFactorsData,
        ],
      }],
    }
  },
)
